import type {
  EamComparisonRow, EamEntraSource, EamMatch, EamPhoneSource, EamReadAccess, EamRealm, EamSignInApp, EamStatus, EamUser,
} from '../types/api'
import { formatDate } from './format'

const TENANT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Strips whitespace and newlines around (and inside) a pasted tenant ID. */
export function normaliseTenantId(value: string): string {
  return value.replace(/\s+/g, '')
}

/** A Microsoft Entra tenant ID is a GUID; nothing else (not even a space) is let through. */
export function isTenantId(value: string): boolean {
  return TENANT_ID.test(value)
}

export interface StatusView {
  /** The "Set up Mideye for your tenant" form. */
  form: boolean
  /** The realm summary strip. */
  summary: boolean
  /** The "being deployed" card; the page polls while it shows. */
  deploying: boolean
  /** Key in, Microsoft registration late: an info card, no polling and no Finish button. */
  waiting: boolean
  /** The "Setup did not finish" card. */
  unfinished: boolean
  /** The three-step guide (consent, the Entra values to paste, turn it on), only once the realm is ready. */
  steps: boolean
}

export function statusView(status: EamStatus): StatusView {
  return {
    form: status === 'none',
    summary: status !== 'none',
    deploying: status === 'activating',
    waiting: status === 'waiting_registration',
    unfinished: status === 'unfinished',
    steps: status === 'ready',
  }
}

/**
 * After a create or finish error: reload the page state? A 409 usually means someone else got
 * there first, so the current state is shown. Not for `no_company_app`: nothing exists yet, and
 * the message (create an API key first) must stay on screen.
 */
export function reloadAfterError(status: number | null, error: string | null): boolean {
  return status === 409 && error !== 'no_company_app'
}

/** Normalised object IDs are lowercase GUIDs. */
export function normaliseObjectId(value: string): string {
  return value.replace(/\s+/g, '').toLowerCase()
}

export function isObjectId(value: string): boolean {
  return TENANT_ID.test(value)
}

// Same separator list as the server's number rule.
const PHONE_SEPARATORS = /(?:[ \t\n\r\f\u00a0\u2007\u202f\u200b\u200c\ufeff\-./\\()\u2010\u2011\u2012\u2013\u2014\u2212])|\u200d/g
const PHONE_FORMAT = /^\+[1-9][0-9]{6,14}$/

export function normalisePhone(value: string): string {
  return value.replace(PHONE_SEPARATORS, '')
}

export function isPhoneFormat(value: string): boolean {
  return PHONE_FORMAT.test(normalisePhone(value))
}

export const PHONE_FORMAT_HINT = 'Start with + and the country code, for example +46 70 123 45 67.'

export const PHONE_SOURCE_LABELS = {
  portal: 'Added on this page',
  property: 'User profile',
  'authentication-methods': 'Authentication methods',
} as const

const CONSENT_HOST = 'https://login.microsoftonline.com/'

/** Defense in depth: the consent button is only shown for a Microsoft sign-in address. */
export function isConsentUrl(url: string | null): url is string {
  return url !== null && url.startsWith(CONSENT_HOST)
}

/** The summary strip's key line, or null while the portal knows no expiry (or an unreadable one). */
export function keyExpiryText(realm: Pick<EamRealm, 'key_expires_at'>): string | null {
  const value = realm.key_expires_at
  if (!value || Number.isNaN(new Date(value).getTime())) return null
  return `Key expires ${formatDate(value)} · renews automatically`
}

const AADSTS = /AADSTS\d+/

export interface ConsentOutcome {
  kind: 'none' | 'returned' | 'error'
  /** For `error`: the `error` value and, if present, the AADSTS code. Nothing else is ever kept. */
  code?: string
  aadsts?: string
}

/**
 * What the consent redirect says. `error` wins over `admin_consent=True`. Only the `error` value and
 * an AADSTS code found in `error_description` are read; no other parameter, and no other part of
 * the description, is ever returned for display.
 */
export function consentOutcome(search: string): ConsentOutcome {
  const params = new URLSearchParams(search)
  const error = params.get('error')
  if (error) {
    const aadsts = AADSTS.exec(params.get('error_description') ?? '')?.[0]
    return { kind: 'error', code: error.slice(0, 64), ...(aadsts ? { aadsts } : {}) }
  }
  return params.get('admin_consent') === 'True' ? { kind: 'returned' } : { kind: 'none' }
}

export interface StepCopy {
  consentTitle: string
  consentBody: string
  turnOnBody: string
  /** Step 3: where each user's number goes; empty when numbers are added on the page. */
  numberItems: Rich[]
}

// Int-keycloak: + or 00 and the country code; Graph answers are cached for 300 s.
const NUMBER_FORMAT: Rich = [
  'Write the number with + and the country code. A changed number can take up to 5 minutes to reach Mideye.',
]
const LIST_WINS: Rich = ['A user on the list below uses the number on the list instead. A user with neither cannot sign in.']

const READ_COPY = {
  consentTitle: '1 · Approve Mideye in your tenant',
  consentBody: 'Needed in every mode. Without it, every sign-in fails.',
  turnOnBody: 'In the same Entra page: Enable → Include → a test group.',
}

const PROPERTY_COPY: StepCopy = {
  ...READ_COPY,
  numberItems: [['Each user needs a number in ', { b: 'Mobile phone' }, ' on their profile. Business phone is not read.'], NUMBER_FORMAT, LIST_WINS],
}

const AUTH_METHODS_COPY: StepCopy = {
  ...READ_COPY,
  numberItems: [
    ['Each user needs a phone sign-in method of type ', { b: 'Mobile' }, '. Alternate mobile and Office numbers are not read.'],
    NUMBER_FORMAT,
    LIST_WINS,
  ],
}

const UNKNOWN_READ_COPY: StepCopy = {
  ...READ_COPY,
  numberItems: [['Each user needs a mobile number in the place you chose.'], NUMBER_FORMAT, LIST_WINS],
}

const PORTAL_COPY: StepCopy = {
  consentTitle: '1 · Approve Mideye in your tenant',
  consentBody: 'Needed in every mode. Without it, every sign-in fails.',
  turnOnBody: 'In the same Entra page: Enable → Include → a test group. Only users on the list below get Mideye MFA.',
  numberItems: [],
}

/** Step copy by phone source; an unknown source reads like the Entra ones, without naming a field. */
export function stepCopy(source: EamPhoneSource | null): StepCopy {
  if (source === 'portal') return PORTAL_COPY
  if (source === 'property') return PROPERTY_COPY
  if (source === 'authentication-methods') return AUTH_METHODS_COPY
  return UNKNOWN_READ_COPY
}

/** The add-user form field an error code belongs to, or null for a general error. */
export function userFieldForError(code: string | null): 'object_id' | 'phone' | null {
  if (code === 'invalid_phone') return 'phone'
  if (code === 'invalid_object_id' || code === 'user_exists') return 'object_id'
  return null
}

export const SIGN_IN_APP_LABELS = { mideye: "Mideye's app", customer: 'Your own app' } as const

const PROPERTY_OPTION = { title: 'User profile → Mobile phone', body: 'The "Mobile phone" field on each user.' }
const AUTH_METHODS_OPTION = { title: 'Authentication methods → Phone', body: 'The mobile number users registered for sign-in.' }

/** The options of the phone-source Change panel; `entra` marks the two that read the directory. */
export const PHONE_SOURCE_OPTIONS: { value: EamPhoneSource; title: string; body: string; entra: boolean }[] = [
  { value: 'property', ...PROPERTY_OPTION, entra: true },
  { value: 'authentication-methods', ...AUTH_METHODS_OPTION, entra: true },
  { value: 'portal', title: 'Added on this page', body: "Mideye gets no access to your directory. You add each user's object ID and mobile number.", entra: false },
]

export const SETUP_CHOICES: {
  id: 'property' | 'authentication-methods' | 'portal-mideye' | 'portal-customer'
  title: string
  body: string
  phoneSource: EamPhoneSource
  signInApp: EamSignInApp
}[] = [
  { id: 'property', ...PROPERTY_OPTION, phoneSource: 'property', signInApp: 'mideye' },
  { id: 'authentication-methods', ...AUTH_METHODS_OPTION, phoneSource: 'authentication-methods', signInApp: 'mideye' },
  { id: 'portal-mideye', title: "Added on this page, Mideye's app", body: "You approve Mideye's app for sign-in only. Mideye cannot read your directory.", phoneSource: 'portal', signInApp: 'mideye' },
  { id: 'portal-customer', title: 'Added on this page, your own app', body: 'You register an app in your tenant. Mideye needs no app and no permission there.', phoneSource: 'portal', signInApp: 'customer' },
]

export const MODE_INFO: Record<(typeof SETUP_CHOICES)[number]['id'], { for: string; against: string }> = {
  property: {
    for: 'No per-user work at Mideye. Uses a field that HR or directory sync often keeps.',
    against: "Mideye can read every user's full profile at any time. An empty field, or a number without a country code, stops that user's MFA.",
  },
  'authentication-methods': {
    for: 'Uses the mobile number the user registered for sign-in.',
    against: "Mideye can read every user's profile and authentication methods at any time. A user without a mobile phone method gets no number.",
  },
  'portal-mideye': {
    for: 'No directory access. Only listed users get Mideye, which suits a pilot.',
    against: 'An admin maintains each number, up to 100 users. Numbers can drift from Entra.',
  },
  'portal-customer': {
    for: 'Nothing of Mideye in your tenant.',
    against: "One app registration and one ID to copy. A changed realm URL means updating your app's redirect URL.",
  },
}

export const MODE_INFO_COPY = { for: 'For:', against: 'Against:' } as const

export function modeInfoLabel(title: string): string {
  return `About ${title}`
}

/** Step 1 on the customer's own app; the redirect URI box shows `entra.redirect_uri`. */
export const ownAppCopy = {
  registerTitle: '1 · Register Mideye in your tenant',
  registerLine: "Your app replaces Mideye's, so Mideye gets no app or permission in your tenant.",
  fieldLabel: 'Application (client) ID',
  placeholder: '00000000-0000-0000-0000-000000000000',
  copyRedirect: 'Copy Redirect URI',
  save: 'Save',
  saving: 'Saving...',
} as const

export const OWN_APP_ON_ENTRA_SOURCE_LINE = "Your app is for sign-in only. Mideye's app still reads phone numbers."

/** The line under "Register": Mideye's app has no part on the portal source only. Not known: no line. */
export function ownAppRegisterLine(phoneSource: EamPhoneSource | null): string | null {
  if (phoneSource === 'portal') return ownAppCopy.registerLine
  if (phoneSource === 'property' || phoneSource === 'authentication-methods') return OWN_APP_ON_ENTRA_SOURCE_LINE
  return null
}

export const REDIRECT_URI_BOX = 'redirect-uri-box' as const
export type OwnAppItem = Rich | typeof REDIRECT_URI_BOX
export interface OwnAppSubStep { readonly title: Rich; readonly items: readonly OwnAppItem[] }

/** Its own export: one line removes it if a dev test shows that no admin consent is needed. */
export const OWN_APP_CONSENT_ITEM: Rich = [{ b: 'Grant admin consent for' }, ' your organisation, then ', { b: 'Yes' }, '.']

const OWN_APP_REGISTER: OwnAppSubStep = {
  title: [{ b: 'Register the app.' }, ' Entra admin center → Entra ID → ', { b: 'App registrations' }, ' → ', { b: 'New registration' }, '.'],
  items: [
    [{ b: 'Name' }, ': for example "Mideye MFA".'],
    [{ b: 'Supported account types' }, ': ', { b: 'Single tenant only' }, ", followed by your organisation's name."],
    [{ b: 'Redirect URI' }, ': in ', { b: 'Select a platform' }, ', choose ', { b: 'Web' }, '. Paste this URL in the field next to it:'],
    REDIRECT_URI_BOX,
    ['Click ', { b: 'Register' }, '.'],
  ],
}

const OWN_APP_PERMISSIONS: OwnAppSubStep = {
  title: [{ b: 'Set the permissions.' }, ' In the new app, open ', { b: 'API permissions' }, '.'],
  items: [
    ['Remove ', { b: 'User.Read' }, ', which Entra adds by default: ', { b: '…' }, ' at the end of its row → ', { b: 'Remove permission' }, ' → ', { b: 'Yes, remove' }, '.'],
    [{ b: 'Add a permission' }, ' → ', { b: 'Microsoft Graph' }, ' → ', { b: 'Delegated permissions' }, '. Under ', { b: 'OpenId permissions' }, ', tick ', { b: 'openid' }, ' and ', { b: 'profile' }, '. Click ', { b: 'Add permissions' }, '.'],
    ['The list now shows ', { b: 'openid' }, ' and ', { b: 'profile' }, ' only, both ', { b: 'Delegated' }, '.'],
    OWN_APP_CONSENT_ITEM,
    ['Leave ', { b: 'Certificates & secrets' }, ' and ', { b: 'Expose an API' }, ' empty: Mideye needs no secret, certificate or exposed API.'],
  ],
}

const OWN_APP_COPY_ID: OwnAppSubStep = {
  title: [{ b: 'Copy the ID.' }, ' In ', { b: 'Overview' }, ', copy the ', { b: 'Application (client) ID' }, '. Paste it below and click ', { b: 'Save' }, '.'],
  items: [],
}

/** The sub-steps; "Show the steps" on a saved ID has no field, so it leaves out the copy-and-paste sub-step. */
export function ownAppSteps(showField: boolean): readonly OwnAppSubStep[] {
  return showField ? [OWN_APP_REGISTER, OWN_APP_PERMISSIONS, OWN_APP_COPY_ID] : [OWN_APP_REGISTER, OWN_APP_PERMISSIONS]
}

export function normaliseAppId(value: string): string {
  return value.replace(/\s+/g, '').toLowerCase()
}

/** The message for an app ID that is not a GUID, or is the tenant ID; null when the shape is fine. */
export function appIdProblem(value: string, tenantId: string): string | null {
  if (!TENANT_ID.test(value)) return 'Paste the Application (client) ID: a GUID.'
  if (value.toLowerCase() === tenantId.toLowerCase()) return 'This is your tenant ID. Paste the Application (client) ID.'
  return null
}

/** `unknown` covers a realm whose app the backend cannot tell and an older backend that sends none. */
export function signInAppView(realm: EamRealm): 'unknown' | 'register' | 'registered' | 'mideye' {
  const app = realm.sign_in_app?.app
  if (!app) return 'unknown'
  if (app === 'mideye') return 'mideye'
  return realm.sign_in_app?.app_id ? 'registered' : 'register'
}

export function moveView(realm: EamRealm): { to: EamSignInApp; appId: string } | null {
  const move = realm.sign_in_app?.moving_to
  return move ? { to: move.app, appId: move.app_id } : null
}

export const SIGN_IN_APP_NOT_KNOWN = 'Not known'
export const SIGN_IN_APP_NOT_KNOWN_STEP = 'The sign-in app is not known. Contact Mideye.'
export const APP_ID_NOT_SAVED = "Save your app's ID in step 1"
export const REPLY_URL_PENDING = "Mideye is registering this realm's reply URL. This takes a few minutes."
export const FINISH_MOVE_LINE = "Finish only after a user has signed in with the method's new values."
export const MOVE_STEP_TWO_LINE = 'Change the Client ID and App ID of your method in Entra to these.'
export const PHONE_SOURCE_OWN_APP_NOTE = "Mideye's app is added to your tenant, to read users. Users keep signing in with your app."
const PHONE_SOURCE_OWN_APP_NOTE_BEFORE_ID = "Mideye's app is added to your tenant, to read users."

export const MOVE_END_NOTES = {
  removeMideyeApp: 'Remove the Mideye EAM enterprise app from your tenant. Mideye then holds nothing there.',
  deleteOwnApp: 'You may now delete your own app registration.',
  deleteOldApp: 'You may delete your old app registration in Entra.',
  changeBack: 'If you already changed your method in Entra, change it back.',
} as const

/** The note for the Entra number sources in the phone-source Change panel; none on Mideye's app and when not known. */
export function phoneSourceEntraNote(realm: EamRealm): string | null {
  const view = signInAppView(realm)
  if (view === 'register') return PHONE_SOURCE_OWN_APP_NOTE_BEFORE_ID
  return view === 'registered' ? PHONE_SOURCE_OWN_APP_NOTE : null
}

/**
 * Step 1 is two independent parts: the own-app part (by sign-in app) and the consent part (by `consent_url`).
 * `mideyeApp`: the consent part is Mideye's app's, on Mideye's app or during a move to it.
 */
export function step1Parts(realm: EamRealm, consentUrl: string | null): {
  unknown: boolean
  ownApp: 'register' | 'registered' | null
  consent: boolean
  mideyeApp: boolean
} {
  const view = signInAppView(realm)
  return {
    unknown: view === 'unknown',
    ownApp: view === 'register' || view === 'registered' ? view : null,
    consent: isConsentUrl(consentUrl),
    mideyeApp: view === 'mideye' || moveView(realm)?.to === 'mideye',
  }
}

export const MOVE_TO_MIDEYE_CONSENT_BODY = "Needed before your method in Entra uses Mideye's app."

/** A heading without its "1 · " prefix, for a part that sits under another part of the same step. */
export function withoutStepNumber(title: string): string {
  return title.replace(/^\d+ · /, '')
}

/** The summary strip's sign-in app text, and whether Change shows (never while unknown or moving). */
export function signInAppStrip(realm: EamRealm): { text: string; canChange: boolean } {
  const move = moveView(realm)
  if (move) return { text: move.to === 'customer' ? 'Moving to your own app' : "Moving to Mideye's app", canChange: false }
  const view = signInAppView(realm)
  if (view === 'unknown') return { text: SIGN_IN_APP_NOT_KNOWN, canChange: false }
  return { text: SIGN_IN_APP_LABELS[view === 'mideye' ? 'mideye' : 'customer'], canChange: true }
}

/** The strip's sign-in Change shows only while the steps show (a ready realm) and the app is known. */
export function signInChangeShown(realm: EamRealm, status: EamStatus): boolean {
  return statusView(status).steps && signInAppStrip(realm).canChange
}

/** "Change the ID" is the own-app target of a registered app, and waits for a move to end. */
export function canChangeAppId(realm: EamRealm): boolean {
  return signInAppView(realm) === 'registered' && moveView(realm) === null
}

/** The sign-in app panel shows while a move runs, or once the admin opened it; never while deploying. */
export function signInPanelOpen(realm: EamRealm | null, deploying: boolean, requested: boolean): boolean {
  return !!realm && !deploying && (moveView(realm) !== null || requested)
}

/**
 * The panel's target: the pick (the admin's, else the one the panel opened on), else the only one on offer.
 * Null leaves nothing selected.
 */
export function panelTarget(targets: EamSignInApp[], pick: EamSignInApp | null): EamSignInApp | null {
  if (pick && targets.includes(pick)) return pick
  return targets.length === 1 ? targets[0] : null
}

/** The open panel's target before Save; null during a move, when the panel shows the move instead. */
export function panelPick(realm: EamRealm, pick: EamSignInApp | null): EamSignInApp | null {
  return moveView(realm) ? null : panelTarget(signInTargets(realm), pick)
}

/** A move of the sign-in app runs, or the open panel has Mideye's app picked. */
export function signInChanging(realm: EamRealm, picked: EamSignInApp | null): boolean {
  return moveView(realm) !== null || picked === 'mideye'
}

/** The phone-source Change waits for a move of the sign-in app to end. */
export function canChangePhoneSource(realm: EamRealm): boolean {
  return moveView(realm) === null
}

/** The targets the sign-in app panel offers; "Change the ID" is the own-app target of a registered app. */
export function signInTargets(realm: EamRealm): EamSignInApp[] {
  const view = signInAppView(realm)
  if (view === 'mideye') return ['customer']
  if (view === 'register') return ['mideye']
  if (view === 'registered') return ['mideye', 'customer']
  return []
}

/** The move to Mideye's app: the consent link (any consent is harmless) and whether the reply URL still waits. */
export function moveToMideyeGate(realm: EamRealm, mideyeAppConsentUrl: string | null | undefined): {
  consentUrl: string | null
  waitingForReplyUrl: boolean
} {
  const url = mideyeAppConsentUrl ?? null
  return { consentUrl: isConsentUrl(url) ? url : null, waitingForReplyUrl: realm.reply_url_registered !== true }
}

export const GRANT_READ_ACCESS_LABEL = 'Grant read access ↗'

/** The v1 link (Entra number source) grants read access; on "Added on this page" it approves sign-in only. */
export function consentLinkLabel(realm: EamRealm): string {
  return isEntraSource(realm.phone_source) ? GRANT_READ_ACCESS_LABEL : 'Approve Mideye ↗'
}

/** The panel's line for the consent link: what it approves. The portal cannot see whether it is done. */
export function mideyeConsentLine(realm: EamRealm): string {
  const what = isEntraSource(realm.phone_source) ? 'for sign-in and read access to your users' : 'for sign-in only'
  return `This approves Mideye's app ${what}. You may have approved it already.`
}

export const REPLY_URL_POLL_MS = 20_000
export const REPLY_URL_POLL_CAP_MS = 15 * 60_000
export const REPLY_URL_STOPPED = 'Reload this page to check again.'

/** Whether the panel still reloads to look for the reply URL, `elapsedMs` after it began waiting. */
export function replyUrlPollDue(elapsedMs: number): boolean {
  return elapsedMs < REPLY_URL_POLL_CAP_MS
}

export type LastMove = {
  kind: 'sign-in' | 'phone-source'
  outcome: 'finished' | 'cancelled'
  from: string
  to: string
}

/** The sign-in move that Finish or Cancel is about to end, read from the realm before the call; null when none runs. */
export function signInMoveEnd(realm: EamRealm, outcome: LastMove['outcome']): LastMove | null {
  const move = moveView(realm)
  const app = realm.sign_in_app?.app
  return move && app ? { kind: 'sign-in', outcome, from: app, to: move.to } : null
}

/** A saved change of the number source; null for an unknown or unchanged source. */
export function phoneSourceMoveEnd(from: EamPhoneSource | null, to: EamPhoneSource): LastMove | null {
  return from && from !== to ? { kind: 'phone-source', outcome: 'finished', from, to } : null
}

/**
 * Step 1's note after the last move ended. It shows only while the realm still matches the move's
 * end state; a move started elsewhere, or a later change, hides it.
 */
export function lastMoveNote(last: LastMove | null, realm: EamRealm): string | null {
  if (!last || moveView(realm)) return null
  if (last.kind === 'phone-source') {
    const fromEntra = last.from === 'property' || last.from === 'authentication-methods'
    const matches = realm.phone_source === last.to && last.to === 'portal' && realm.sign_in_app?.app === 'customer'
    return fromEntra && matches ? MOVE_END_NOTES.removeMideyeApp : null
  }
  const app = realm.sign_in_app?.app
  if (last.outcome === 'cancelled') return app === last.from ? MOVE_END_NOTES.changeBack : null
  if (app !== last.to) return null
  if (last.to === 'mideye') return MOVE_END_NOTES.deleteOwnApp
  if (last.from === 'customer') return MOVE_END_NOTES.deleteOldApp
  return realm.phone_source === 'portal' ? MOVE_END_NOTES.removeMideyeApp : null
}

export const DISMISS_LABEL = 'Dismiss'

/** What a refused (409) sign-in-app call says; the page has already reloaded the realm. */
export function signInRefusalNotice(code: string | null): string {
  switch (code) {
    case 'move_changed':
      return 'The move changed in another tab or by another admin. Nothing was saved. Check the sign-in app before you change your method in Entra.'
    case 'move_in_progress':
      return 'A move is already in progress. Nothing was saved. Finish or cancel it first.'
    case 'same_app':
      return 'The realm already uses this app. Nothing was saved.'
    case 'reply_url_pending':
      return "Mideye's app is not ready for this realm yet. Nothing was saved. Try again in a few minutes."
    case 'sign_in_app_unknown':
      return 'The sign-in app is not known. Nothing was saved. Contact Mideye.'
    case 'not_ready':
      return 'The realm is not ready yet. Nothing was saved.'
    default:
      return 'The realm changed since this page loaded. Nothing was saved. Check it before you continue.'
  }
}

/** The notice for a failed sign-in-app call that the page answers by reloading; null for any other failure. */
export function signInRefusal(status: number | null, error: string | null): string | null {
  return reloadAfterError(status, error) ? signInRefusalNotice(error) : null
}

export type Rich = readonly (string | { readonly b: string })[]

export const signInPanelCopy = {
  heading: 'Sign-in app',
  newAppId: 'New app ID:',
  cancelMove: 'Cancel the move',
  cancelling: 'Cancelling…',
  finishMove: 'Finish the move',
  finishing: 'Finishing…',
  targetLegend: 'Move the sign-in app to',
  ownAppTarget: 'Your own app (new ID)',
  moveTo: 'Move to',
  save: 'Save',
  saving: 'Saving...',
  close: 'Close',
} as const

/** The panel's heading during a move. */
export function movingHeading(realm: EamRealm): string {
  return `${signInPanelCopy.heading} · ${signInAppStrip(realm).text}`
}

export const STRIP_SIGN_IN_APP_LABEL = 'Sign-in app'

export const stepsCopy = {
  unknownTitle: '1 · Sign-in app',
  registeredPrefix: '✓ Your app:',
  showSteps: 'Show the steps',
  hideSteps: 'Hide the steps',
  changeId: 'Change the ID',
  grantConsent: 'Grant consent in Microsoft ↗',
  step2Title: '2 · Add external MFA in Entra',
  step2Intro: ['Entra admin center → Entra ID → Authentication methods → ', { b: 'Add external MFA' }, '. Paste these:'] as Rich,
  rowLabels: { name: 'Name', clientId: 'Client ID', discovery: 'Discovery Endpoint', appId: 'App ID' },
  copyTitle: (label: string) => `Copy ${label}`,
  step3Title: '3 · Turn it on for a test group',
  step3Items: [
    ['Enable stays greyed until the consent in step 1 is granted.'],
    ['If Include lists ', { b: 'All users' }, ', remove it before you add the test group.'],
    ['If another external MFA method includes the same users, exclude the test group from it. Otherwise Entra can send them to that method.'],
    ['Add more groups once the test group works.'],
  ] as Rich[],
  step4Title: '4 · Require MFA for the group',
  step4Items: [
    ['A Conditional Access policy that grants with ', { b: 'Require multifactor authentication' }, ' sends the group to Mideye. A policy you already have for these users is enough.'],
    ['No authentication strength accepts Mideye, the built-in Multifactor authentication strength included.'],
  ] as Rich[],
  step5Title: '5 · Put Mideye first',
  step5Items: [
    ['Once a user has registered Mideye, Entra opens it before SMS and voice calls.'],
    ['A user with a passkey or Microsoft Authenticator may get that method first. If so, exclude the group from it: Authentication methods → Policies → ', { b: 'Passkey (FIDO2)' }, ' or ', { b: 'Microsoft Authenticator' }, ' → Exclude.'],
    ['The group can then no longer use that method. Keep emergency access accounts out of the group.'],
  ] as Rich[],
  step6Title: '6 · Each user registers Mideye once',
  step6Intro: 'Entra offers Mideye only to users who have registered it. No setting registers a whole group.',
  step6Items: [
    ['An admin adds it for each user: Users → the user → Authentication methods → ', { b: 'Add authentication method' }, ', then the external MFA method from step 2. The user then lists External MFA under Usable authentication methods.'],
    ['A user who already has another sign-in method, such as SMS, adds it once: Security info → ', { b: 'Add sign-in method' }, ' → External MFA.'],
    ['Password reset → Registration → ', { b: 'Require users to register when signing in' }, " → No. A user with no sign-in methods then picks External MFA in Entra's setup wizard. The first sign-in takes two Mideye approvals: one registers Mideye, one signs the user in. The change can take some minutes to take effect."],
    ['No applies to the whole tenant: Entra stops asking users for a reset method at sign-in, and methods they have stay. Mideye does not reset passwords, so a user with Mideye only cannot reset their own password. If you keep Yes, Entra discards Mideye in the wizard for a user without a reset method ("Verification failed"). That user adds a phone first, then Mideye as above.'],
  ] as Rich[],
} as const

/** Step 2's Client ID and App ID: the value, or why there is none. */
export function stepTwoValue(value: string | null, realm: EamRealm): string {
  if (value) return value
  return signInAppView(realm) === 'register' ? APP_ID_NOT_SAVED : SIGN_IN_APP_NOT_KNOWN
}

/** The app ID field's backend errors; they show under the field, not as a page error. */
export function isAppIdFieldError(code: string | null): boolean {
  return code === 'invalid_app_id' || code === 'app_id_is_tenant' || code === 'app_id_is_mideye' || code === 'app_id_in_use'
}

/** The method ID field's backend errors; they show under the field. */
export function isMethodIdFieldError(code: string | null): boolean {
  return code === 'invalid_method_id' || code === 'keycloak_failed' || code === 'unavailable'
}

export const registerCopy = {
  button: 'Register Mideye for these users',
  consent:
    'Sign in as a Global Administrator. Microsoft asks you to allow one more permission: register external MFA for users. Leave "Consent on behalf of your organization" unticked. The permission is then for your account only.',
  token: "Mideye's page uses the token in your browser; Mideye's servers never receive it.",
  finishMoveFirst: 'Finish the move between sign-in apps first. Then register Mideye for these users.',
  methodIdLabel: 'Entra method ID',
  // To be rewritten after dev test 1 (step 3).
  methodIdHint: 'The ID of the external method from step 2, in the Entra admin center.',
  methodIdInvalid: 'Paste the method ID: a GUID.',
  save: 'Save',
  cancel: 'Cancel',
  change: 'Change',
  noNumber: 'No number',
  headings: { phone: 'Mobile number', objectId: 'Object ID', result: 'Result' },
  progress: (done: number, total: number) => `Registering ${Math.min(done + 1, total)} of ${total}`,
  summary: (now: number, already: number, notRegistered: number) =>
    `${now} registered now, ${already} registered already, ${notRegistered} not registered.`,
  runStopped: (message: string) => 'The run stopped: ' + message,
  results: {
    registered_now: 'Registered now',
    registered_already: 'Registered already',
    other_tenant: 'Not registered: this user is in another tenant.',
    no_role:
      "Not registered: your role cannot change this user's methods. Users with admin roles need Privileged Authentication Administrator.",
    not_found: 'Not registered: Entra has no user with this object ID.',
    not_run: 'Not tried.',
  },
  failed: (detail: string | null) => 'Not registered: ' + (detail ?? ''),
  stops: {
    no_role: 'Your account needs Authentication Administrator. Nothing was registered.',
    bad_method_id: (detail: string | null) =>
      'The method ID is wrong, or the method is off. Check step 2 and the ID. Graph said: ' + (detail ?? ''),
  },
  signIn: {
    cancelled: 'Sign-in or consent was cancelled. Nothing was registered.',
    popup_blocked: 'Your browser blocked the Microsoft sign-in window. Allow pop-ups for this page and try again.',
    other: (message: string) => 'The Microsoft sign-in failed. Nothing was registered. Microsoft said: ' + message,
  },
  removeLink: 'Remove the permission',
  // Not verified; dev test 8 confirms the path. #156 uses the same name and link.
  removeUrl: 'https://entra.microsoft.com/#view/Microsoft_AAD_IAM/StartboardApplicationsMenuBlade/~/AppAppsPreview',
  removePath: 'Enterprise applications → the Mideye EAM app → Permissions → User consent.',
} as const

export const READ_ROLES: Record<EamEntraSource, string[]> = {
  property: ['User.Read.All'],
  'authentication-methods': ['User.Read.All', 'UserAuthenticationMethod.Read.All'],
}

const ALL_READ_ROLES = READ_ROLES['authentication-methods']

export function isEntraSource(source: EamPhoneSource | null): source is EamEntraSource {
  return source === 'property' || source === 'authentication-methods'
}

/** The roles the source needs that the app lacks; null when the check gave no answer. */
export function missingRoles(access: EamReadAccess | null, source: EamEntraSource): string[] | null {
  if (!access || access.status === 'unknown') return null
  if (access.status === 'not_approved') return [...READ_ROLES[source]]
  return READ_ROLES[source].filter((role) => !access.roles.includes(role))
}

export type ConsentTone = 'ok' | 'warn' | 'bad' | 'grey'
export interface ConsentStatusView {
  label: string
  tone: ConsentTone
  notes: string[]
}

const NOT_ON_LIST_NOTE = 'Users not on the list cannot sign in. Grant read access.'
const STILL_GRANTED_NOTE = 'Read access still granted. Not used in this mode. Remove it in Entra.'

/**
 * Step 1's read-access tag and notes. The roles are judged against the source being moved to,
 * else the current Entra one. Null means no tag.
 */
export function consentStatus(
  access: EamReadAccess | null,
  current: EamPhoneSource | null,
  pending: EamEntraSource | null,
  ownApp: boolean,
): ConsentStatusView | null {
  const src = pending ?? (isEntraSource(current) ? current : null)
  if (ownApp && !src) return null
  if (!access) return { label: 'Checking…', tone: 'grey', notes: [] }
  if (access.status === 'unknown') return { label: 'Not known', tone: 'grey', notes: [] }
  if (!ownApp && access.status === 'not_approved') return { label: 'Not approved', tone: 'bad', notes: [] }
  if (!src) {
    if (!ALL_READ_ROLES.some((role) => access.roles.includes(role))) {
      return { label: 'Approved', tone: 'ok', notes: [] }
    }
    return current === 'portal'
      ? { label: 'Approved · read access', tone: 'warn', notes: [STILL_GRANTED_NOTE] }
      : { label: 'Approved · read access', tone: 'ok', notes: [] }
  }
  const missing = missingRoles(access, src) ?? []
  if (missing.length === 0) return { label: ownApp ? 'Read access granted' : 'Approved · read access', tone: 'ok', notes: [] }
  const notes = [`Missing: ${missing.join(', ')}`]
  if (isEntraSource(current) && (missingRoles(access, current) ?? []).length > 0) notes.push(NOT_ON_LIST_NOTE)
  return { label: ownApp ? 'No read access' : 'Approved', tone: 'warn', notes }
}

/**
 * The own app on "Added on this page" with Mideye's app still approved; the page skips `lastMoveNote`'s equal line.
 * Not while `signInChanging`: the admin may be going back to Mideye's app.
 */
export function mideyeAppLeftover(
  access: EamReadAccess | null,
  current: EamPhoneSource | null,
  pending: EamEntraSource | null,
  ownApp: boolean,
  signInChanging: boolean,
): string | null {
  return ownApp && !signInChanging && current === 'portal' && pending === null && access?.status === 'approved'
    ? MOVE_END_NOTES.removeMideyeApp
    : null
}

/** Step 1's move note: none when the leftover line says the same, and no "remove Mideye's app" while the panel picks it. */
export function step1Note(note: string | null, leftover: string | null, picked: EamSignInApp | null): string | null {
  if (note === leftover) return null
  return picked === 'mideye' && note === MOVE_END_NOTES.removeMideyeApp ? null : note
}

/**
 * Whether the consent poll can stop: Mideye's app is approved and holds the roles of the source being
 * moved to, else of the current Entra source. With neither, `approved` alone settles it.
 */
export function readAccessSettled(
  access: EamReadAccess | null,
  current: EamPhoneSource | null,
  pending: EamEntraSource | null,
): boolean {
  if (access?.status !== 'approved') return false
  const target = pending ?? (isEntraSource(current) ? current : null)
  return !target || missingRoles(access, target)?.length === 0
}

const MATCH_LABELS: Record<EamMatch, string> = {
  same: 'Same',
  differs: 'Differs · this page wins',
  entra_unusable: "Entra's unusable · this page covers it",
  not_in_tenant: 'Not in your tenant',
  page_unusable: "No number here · Entra's used",
  no_number: 'No usable number · sign-in fails',
  unknown: 'Not known',
}

export function matchLabel(status: EamMatch): string {
  return MATCH_LABELS[status]
}

export interface MergedUser {
  user: EamUser
  row: EamComparisonRow
}

/** Every listed user, in list order; one the comparison lacks reads as not known. */
export function mergeComparison(users: EamUser[], rows: EamComparisonRow[] | null): MergedUser[] {
  const byId = new Map((rows ?? []).map((row) => [row.id, row]))
  return users.map((user) => ({
    user,
    row: byId.get(user.id) ?? { id: user.id, display_name: null, upn: null, entra_phone: null, status: 'unknown' },
  }))
}

export function comparisonFigures(merged: MergedUser[]): { listed: number; differ: number; notInTenant: number } {
  return {
    listed: merged.length,
    differ: merged.filter((m) => m.row.status === 'differs').length,
    notInTenant: merged.filter((m) => m.row.status === 'not_in_tenant').length,
  }
}

/** What "Remove the n that match Entra" removes: enabled users of this tenant with the same number. */
export function matchingCount(merged: MergedUser[]): number {
  return merged.filter((m) => m.row.status === 'same' && m.user.enabled && !m.user.other_tenant).length
}

export interface UsersCardView {
  title: string
  /** Show the full comparison: User, both numbers, Status and the figures. */
  comparing: boolean
  /** Show the "Number in Entra" column; when the comparison is not known it stands beside today's columns. */
  entraColumn: boolean
  /** The n for "Remove the n that match Entra"; null hides the action. */
  removeMatching: number | null
  emptyLine: string | null
}

/**
 * What the Users card shows. The title and empty line follow the current source; the comparison follows
 * `compareSource`. During a review (compareSource differs from the current source) nothing is removed:
 * on "Added on this page" a removed user loses Mideye at once. Null `merged` is no usable comparison:
 * `notKnown` says it was asked for and failed, so today's columns stay; otherwise it is still loading.
 */
export function usersCardView(
  phoneSource: EamPhoneSource | null,
  compareSource: EamEntraSource | null,
  merged: MergedUser[] | null,
  listed: number,
  notKnown = false,
): UsersCardView {
  const entra = isEntraSource(phoneSource)
  const review = compareSource !== null && compareSource !== phoneSource
  const matching = merged ? matchingCount(merged) : 0
  return {
    title: entra ? "Numbers that replace Entra's" : 'Users',
    comparing: compareSource !== null && !notKnown,
    entraColumn: compareSource !== null,
    removeMatching: entra && !review && matching > 0 ? matching : null,
    emptyLine: entra && listed === 0 ? 'All numbers come from Entra' : null,
  }
}

export const PENDING_SOURCE_KEY = 'eam.pendingSource'

export function readPendingSource(storage: Storage | null): EamEntraSource | null {
  try {
    const value = (storage?.getItem(PENDING_SOURCE_KEY) ?? null) as EamPhoneSource | null
    return isEntraSource(value) ? value : null
  } catch {
    return null
  }
}

export function writePendingSource(storage: Storage | null, source: EamEntraSource | null): void {
  try {
    if (source === null) storage?.removeItem(PENDING_SOURCE_KEY)
    else storage?.setItem(PENDING_SOURCE_KEY, source)
  } catch {
    // Storage is a convenience: the move works without it.
  }
}

export const CONSENT_INFO = {
  signInOnlyLabel: 'Sign-in only:',
  readAccessLabel: 'Read access:',
  signInOnly: [
    'The consent screen lists "View users\' basic profile" and "Maintain access to data you have given it access to".',
    'The Permissions page shows "Sign users in" and "View users\' basic profile".',
  ],
  readAccess: ['The consent screen lists "Read all users\' full profiles" and "Read all users\' authentication methods".'],
  adminConsent: 'Admin consent means one approval, for every user in the organisation.',
  removeLink: registerCopy.removeLink,
  removeUrl: registerCopy.removeUrl,
  removePath: "Enterprise applications → the Mideye EAM app → Permissions → Admin consent. Each row's … menu has Revoke permission.",
} as const

/** The `MODE_INFO` key for a phone source; "Added on this page" depends on the sign-in app. */
export function modeInfoIdFor(source: EamPhoneSource, signInApp: EamSignInApp | null): (typeof SETUP_CHOICES)[number]['id'] {
  if (source !== 'portal') return source
  return signInApp === 'customer' ? 'portal-customer' : 'portal-mideye'
}

export type PhoneSourceMoveAction = 'checking' | 'grant' | 'switch' | 'switch-unknown'

export const SWITCH_UNKNOWN_NOTE =
  'Mideye could not check read access. Users not on the list cannot sign in until a Global Administrator grants it.'

/** What the Change panel offers for a move to an Entra source; a missing consent link reads as "not known". */
export function phoneSourceMoveAction(
  access: EamReadAccess | null,
  target: EamEntraSource,
  consentUrl: string | null | undefined,
): PhoneSourceMoveAction {
  if (access === null) return 'checking'
  const missing = missingRoles(access, target)
  if (missing === null) return 'switch-unknown'
  if (missing.length === 0) return 'switch'
  return consentUrl ? 'grant' : 'switch-unknown'
}

/** The line before Save when the move is to "Added on this page"; `count` is null until the list is loaded. */
export function portalMoveLine(count: number | null): string {
  if (count === null) return 'Only the users on this page keep Mideye'
  return count === 1 ? 'Only the 1 user on this page keeps Mideye' : `Only the ${count} users on this page keep Mideye`
}
